<template>
  <ApplicationLayout>
    <v-container
      fluid
      px-12
      py-6
      v-if="ready"
    >
      <div
        class="
          d-flex
          align-center
        "
      >
        <BackTitle
          :to="{
            name: 'feeds',
            query: { focus: flux.id }
          }"
          :title="title"
          :subtitle="fluxName"
        ></BackTitle>

        <v-spacer></v-spacer>

        <CancelFluxButton
          v-if="flux.id"
          :buttonProps="{
            'x-large': true
          }"
          :flux="flux"
        ></CancelFluxButton>

        <ProcessFluxButton
          v-if="flux.id"
          :buttonProps="{
            'x-large': true
          }"
          :flux="flux"
        ></ProcessFluxButton>
      </div>

      <v-card
        class="pa-6 mt-6"
      >
        <v-form
          @submit.prevent="submit()"
          ref="form"
          v-model="valid"
        >
          <v-row>
            <v-col
              cols="6"
              class="px-6"
            >
              <v-text-field
                v-model="flux.name"
                label="Name"
                :rules="rules.name"
              ></v-text-field>

              <v-select
                v-model="flux.status"
                :items="statuses"
                label="Status"
              ></v-select>

              <Combobox
                v-model="flux.brand_id"
                :select-props="{
                  items: _brands,
                  label: flux.auto_detect_brand
                    ? 'Default brand (when not found in feed)'
                    : 'Brand',
                  clearable: true,
                  hideDetails: true
                }"
                @create-new-item="
                  $store.dispatch('Brands/createAndSet', {
                    name: $event,
                    item: flux
                  })
                "
              ></Combobox>

              <v-switch
                v-model="flux.auto_detect_brand"
                label="Auto-detect brand from flux"
              ></v-switch>
            </v-col>

            <v-col
              cols="6"
              class="px-6"
            >
              <v-text-field
                v-model="flux.url"
                label="URL"
                :rules="rules.url"
              ></v-text-field>

              <v-select
                v-model="flux.provider"
                :items="providers"
                label="Provider"
                :rules="rules.provider"
              ></v-select>

              <h4 class="subtitle-1 mt-6">Admin fields</h4>

              <Combobox
                v-model="flux.reseller_id"
                :select-props="{
                  items: _resellers,
                  label: 'Reseller',
                  clearable: true
                }"
                @create-new-item="$store.dispatch('Resellers/createAndSet', { name: $event, item: flux })"
              ></Combobox>
            </v-col>
          </v-row>

          <v-row
            justify="end"
          >
            <v-btn
              :disabled="!modified"
              v-if="editMode"
              class="mr-4"
              @click="cancel()"
              depressed
            >Cancel</v-btn>

            <v-btn
              :disabled="!valid || !modified"
              class="mr-4"
              color="primary"
              type="submit"
              depressed
            >Save</v-btn>

            <v-btn
              :disabled="!valid || !modified"
              color="primary"
              @click="saveAndGoBack()"
              depressed
            >Save & Go back</v-btn>
          </v-row>
        </v-form>
      </v-card>

      <ProductsList
        v-if="flux.id"
        class="mt-12"
        title="Feed products"
        :scope="{ fluxId: flux.id }"
      ></ProductsList>
    </v-container>
  </ApplicationLayout>
</template>

<script>
import { mapState } from 'vuex'
import ApplicationLayout from '../components/layouts/ApplicationLayout.vue'
import Combobox from '../components/Combobox.vue'
import BackTitle from '../components/BackTitle.vue'
import ProductsList from '../components/ProductsList.vue'
import ProcessFluxButton from '../components/ProcessFluxButton.vue'
import CancelFluxButton from '../components/CancelFluxButton.vue'

export default {
  components: {
    ApplicationLayout,
    Combobox,
    BackTitle,
    ProductsList,
    ProcessFluxButton,
    CancelFluxButton
  },
  data() {
    return {
      valid: false,
      ready: false,
      editMode: !!this.$route.params.id,
      fluxCopy: null,
      providers: [
        {
          value: 'lengow_xml',
          text: this.$translate.instant('FLUX_LENGOW_XML')
        },
        {
          value: 'google_shopping_feed',
          text: this.$translate.instant('FLUX_GOOGLE_SHOPPING_FEED')
        },
        {
          value: 'awin_xml',
          text: this.$translate.instant('FLUX_AWIN_XML')
        }, {
          value: 'tradedoubler_xml',
          text: this.$translate.instant('FLUX_TRADEDOUBLER_XML')
        }
      ],
      statuses: [
        {
          value: 'disabled',
          text: this.$translate.instant('FLUX_DISABLED')
        },
        {
          value: 'enabled',
          text: this.$translate.instant('FLUX_ENABLED')
        }
      ],
      rules: {
        name: [v => !!v || 'Name is required'],
        url: [
          v => !!v || 'URL is required',
          v => /https?:\/\/.+/.test(v) || 'URL must be valid'
        ],
        provider: [v => !!v || 'Provider is required']
      }
    }
  },
  computed: {
    ...mapState('Fluxes', ['flux', 'fluxName']),
    ...mapState('Resellers', ['resellers']),
    ...mapState('Brands', ['brands']),
    title() {
      return this.editMode ? 'Feed' : 'Create new feed'
    },
    modified() {
      const flux = { ...this.flux }
      delete flux.state
      return this.fluxCopy !== this.getFluxString()
    },
    _resellers() {
      return this.resellers.map(reseller => ({
        text: reseller.name,
        value: reseller.id
      }))
    },
    _brands() {
      return this.brands.map(brand => ({
        text: brand.name,
        value: brand.id
      }))
    }
  },
  async created() {
    await this.getFlux(this.$route.params.id)
    this.$store.dispatch('Resellers/getAll')
    this.$store.dispatch('Brands/getAll')
    this.ready = true
  },
  methods: {
    getFluxString() {
      const flux = { ...this.flux }
      delete flux.state
      return JSON.stringify(flux)
    },
    async getFlux(id) {
      await this.$store.dispatch('Fluxes/get', id)
      this.fluxCopy = this.getFluxString()
    },
    cancel() {
      this.$store.commit('Fluxes/setFlux', JSON.parse(this.fluxCopy))
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      const snackbar = { text: 'Feed update succeed' }
      try {
        await this.$store.dispatch('Fluxes/createOrUpdate')
        if (!this.editMode) {
          this.$router.push({ name: 'feed', params: { id: this.flux.id } })
          snackbar.text = 'Feed creation succeed'
          this.editMode = true
        }
        this.fluxCopy = this.getFluxString()
      } catch (error) {
        snackbar.text = this.editMode ? 'Feed update failed' : 'Feed creation failed'
        snackbar.color = 'error'
      }
      this.$store.commit('Global/setSnackbar', snackbar)
    },
    async saveAndGoBack() {
      await this.submit()
      this.$router.push({ name: 'feeds' })
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.name === from.name && to.params.id !== from.params.id) {
      await this.getFlux(to.params.id)
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (from.name !== 'new-feed') {
      this.$store.commit('Fluxes/setFlux', null)
      this.$store.commit('Products/setProducts', null)
    }
    next()
  }
}
</script>
